<script setup lang="ts">
import { BellIcon, CheckIcon, MapPinIcon, UserIcon } from '@heroicons/vue/24/solid';
import { publicTicketApi } from '@libero/api-client/public-ticket/api';
import { StorePublicTicketRequest } from '@libero/api-client/public-ticket/types';
import { Ticket } from '@libero/api-client/ticket/types';
import { useMapContext } from '@libero/citizen-report/modules/map/hooks/useMap';
import Location from '@libero/citizen-report/modules/report/views/steps/Location.vue';
import Report from '@libero/citizen-report/modules/report/views/steps/Report.vue';
import Reporter from '@libero/citizen-report/modules/report/views/steps/Reporter.vue';
import Thanks from '@libero/citizen-report/modules/report/views/steps/Thanks.vue';
import { useForm } from '@libero/hooks/useForm';
import Form from '@libero/ui-framework/Form/Form.vue';
import Stack from '@libero/ui-framework/Stack/Stack.vue';
import Steps from '@libero/ui-framework/Steps/Steps.vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterView } from 'vue-router';

interface Props {
  hasInlineMap: boolean;
}

defineProps<Props>();

const queryClient = useQueryClient();
const { t } = useI18n();
const map = useMapContext();

const ticket = ref<Ticket>();
const step = ref(0);
const steps = [Report, Location, Reporter, Thanks];

const form = useForm<StorePublicTicketRequest>({
  theme_id: null,
  ticket_subject_id: null,
  should_continue_high_priority: false,
  has_explanation: false,
  require_clarification: false,
  explanation: null,
  location: null,
  force_create: false,
  containers: [],
  media: [],
  reporter: {
    has_email: false,
    email: null,
    phone: null,
    accept_terms: false,
  },
});

const { mutateAsync, isPending } = useMutation({
  mutationFn: publicTicketApi.store,
  onSuccess: (savedTicket) => {
    queryClient.invalidateQueries({ queryKey: ['map'] });
    ticket.value = savedTicket;
    map.setThemeId();
    handleNextStep();
  },
});

const handleSubmit = () => {
  form.submit(mutateAsync);
};

const handleUpdateStep = (value: number) => {
  step.value = value + 1;
};

const handlePreviousStep = () => {
  step.value--;
};

const handleNextStep = () => {
  step.value++;
};

watch(step, () => {
  form.clearErrors();
  const element = document.querySelector('.app-layout-content');
  if (element) element.scrollTop = 0;
  map.setBlocked(step.value !== 1);
});
</script>

<template>
  <Stack :gap="1.5">
    <Steps
      :value="step"
      :items="[
        { title: t('citizen-report.steps.report'), icon: BellIcon },
        { title: t('citizen-report.steps.location'), icon: MapPinIcon },
        { title: t('citizen-report.steps.reporter'), icon: UserIcon },
        { title: t('citizen-report.steps.thanks'), icon: CheckIcon },
      ]"
      :onUpdate="handleUpdateStep"
    />

    <Stack flex="1">
      <Form v-bind="form.registerForm(handleSubmit)">
        <component
          :is="steps[step]"
          :isLoading="isPending"
          :ticket="ticket"
          :hasInlineMap="hasInlineMap"
          :onPreviousStep="handlePreviousStep"
          :onNextStep="handleNextStep"
        />
      </Form>
    </Stack>
  </Stack>

  <RouterView />
</template>
